// src/pages/employee/BuyerManagement.js
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { AuthContext } from '../../components/AuthContext';

const BuyerManagement = () => {
  const { user } = useContext(AuthContext);
  const [buyers, setBuyers] = useState([]);
  const [stations, setStations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalBuyers, setTotalBuyers] = useState(0);
  const limit = 10;
  
  const [form, setForm] = useState({
    businessName: '',
    businessOwner: '',
    phone: '',
    secondaryPhone: '',
    email: '',
    managerName: '',
    address: '',
    station: '',
    pinCode: '',
    gstNo: '',
    panNo: '',
    createdBy: user?.user?._id
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Modal state for viewing dealer details
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedDealer, setSelectedDealer] = useState(null);

  useEffect(() => {
    fetchBuyers();
    fetchStations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchQuery]);

  const fetchBuyers = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/buyers`, {
        params: { page, limit, search: searchQuery }
      });
      setBuyers(response.data.buyers);
      setTotalBuyers(response.data.total);
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch buyers', 'error');
    }
  };

  const fetchStations = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/stations`);
      setStations(response.data);
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch stations', 'error');
    }
  };

  const validateForm = () => {
    if (!form.businessName || !form.phone || !form.station) {
      Swal.fire('Error', 'Business name, primary phone, and station are required', 'error');
      return false;
    }
    if (form.phone.length !== 10) {
      Swal.fire('Error', 'Primary phone number should be 10 digits', 'error');
      return false;
    }
    if (form.secondaryPhone && form.secondaryPhone.length !== 10) {
      Swal.fire('Error', 'Secondary phone number should be 10 digits if provided', 'error');
      return false;
    }
    return true;
  };

  const resetForm = () => {
    setForm({
      businessName: '',
      businessOwner: '',
      phone: '',
      secondaryPhone: '',
      email: '',
      managerName: '',
      address: '',
      station: '',
      pinCode: '',
      gstNo: '',
      panNo: '',
      createdBy: user?.user?._id
    });
    setIsEditing(false);
    setEditingId(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleAddOrUpdateBuyer = async () => {
    if (!validateForm()) return;
    
    try {
      if (isEditing) {
        await axios.put(`${process.env.REACT_APP_API_BASE_URL}/buyers/${editingId}`, {
          ...form,
          updatedBy: user.user._id
        });
        Swal.fire('Success', 'Buyer updated successfully', 'success');
      } else {
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/buyers`, form);
        Swal.fire('Success', 'Buyer added successfully', 'success');
      }
      fetchBuyers();
      resetForm();
    } catch (error) {
      Swal.fire('Error', 'Failed to save buyer', 'error');
    }
  };

  const startEditing = (buyer) => {
    setIsEditing(true);
    setEditingId(buyer._id);
    setForm(buyer);
  };

  // Handler to open modal for dealer view
  const handleViewDealer = (dealer) => {
    setSelectedDealer(dealer);
    setModalVisible(true);
  };

  const totalPages = Math.ceil(totalBuyers / limit);

  return (
    <div className="p-6 bg-white shadow rounded-lg">
      <h2 className="text-3xl font-bold text-gray-800 mb-4">Buyer Management</h2>

      {/* Form Section */}
      <div className="mb-6">
        <h3 className="text-2xl font-semibold text-gray-700 mb-2">
          {isEditing ? 'Edit Buyer' : 'Add New Buyer'}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.keys(form)
            .filter(key => !['createdBy', 'id', 'createdAt', 'updatedAt', 'updatedBy', '__v', '_id', 'state'].includes(key))
            .map((key) => (
              <div key={key} className="flex flex-col">
                <label htmlFor={key} className="text-gray-700 font-semibold mb-2">
                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
                </label>
                {key === "station" ? (
                  <select
                    name="station"
                    value={form.station}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">Select Station</option>
                    {stations.map((station) => (
                      <option key={station._id} value={station._id}>
                        {station.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id={key}
                    type={key === 'email' ? 'email' : 'text'}
                    name={key}
                    value={form[key]}
                    onChange={handleInputChange}
                    maxLength={(key === 'phone' || key === 'secondaryPhone') ? 10 : undefined}
                    placeholder={`Enter ${key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}`}
                    className="border rounded p-2 w-full focus:outline-blue-500"
                  />
                )}
              </div>
            ))}
        </div>
        <div className="mt-4">
          <button
            onClick={handleAddOrUpdateBuyer}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            {isEditing ? 'Update Buyer' : 'Add Buyer'}
          </button>
          {isEditing && (
            <button
              onClick={resetForm}
              className="ml-2 bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Search Bar (after Form) */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by id, name, phone, email, GST or PAN..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1);
          }}
          className="border border-blue-500 rounded p-2 w-full focus:outline-blue-500"
        />
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 bg-white shadow rounded-lg">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase">ID</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase">Business Name</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase">Primary Phone</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase">Secondary Phone</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase">Email</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase">GST Number</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase">PAN Number</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 uppercase">Station</th>
              <th className="px-4 py-2 text-center text-sm font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {buyers.map((buyer) => (
              <tr key={buyer._id}>
                <td className="px-4 py-2 text-sm text-gray-900">{buyer.id || '-'}</td>
                <td className="px-4 py-2 text-sm text-gray-900">{buyer.businessName || '-'}</td>
                <td className="px-4 py-2 text-sm text-gray-500">{buyer.phone || '-'}</td>
                <td className="px-4 py-2 text-sm text-gray-500">{buyer.secondaryPhone || '-'}</td>
                <td className="px-4 py-2 text-sm text-gray-500">{buyer.email || '-'}</td>
                <td className="px-4 py-2 text-sm text-gray-500">{buyer.gstNo || '-'}</td>
                <td className="px-4 py-2 text-sm text-gray-500">{buyer.panNo || '-'}</td>
                <td className="px-4 py-2 text-sm text-gray-500">{buyer.station?.name || '-'}</td>
                <td className="px-4 py-2 text-center text-sm font-medium">
                  <button
                    onClick={() => startEditing(buyer)}
                    className="text-yellow-600 hover:text-yellow-900 mr-2"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleViewDealer(buyer)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Grid Layout */}
      <div className="grid grid-cols-1 gap-4 md:hidden">
        {buyers.map((buyer) => (
          <div key={buyer._id} className="p-4 bg-white rounded-lg shadow border">
            <div className="mb-2">
              <h3 className="text-lg font-bold text-gray-900">{buyer.businessName || '-'}</h3>
              <p className="text-sm text-gray-500">ID: {buyer.id || '-'}</p>
            </div>
            <div className="space-y-1 text-sm text-gray-600">
              <p><strong>Primary Phone:</strong> {buyer.phone || '-'}</p>
              <p><strong>Secondary Phone:</strong> {buyer.secondaryPhone || '-'}</p>
              <p><strong>Email:</strong> {buyer.email || '-'}</p>
              <p><strong>GST:</strong> {buyer.gstNo || '-'}</p>
              <p><strong>PAN:</strong> {buyer.panNo || '-'}</p>
              <p><strong>Station:</strong> {buyer.station?.name || '-'}</p>
            </div>
            <div className="mt-3 flex justify-between">
              <button
                onClick={() => startEditing(buyer)}
                className="text-yellow-600 hover:text-yellow-900 text-sm"
              >
                Edit
              </button>
              <button
                onClick={() => handleViewDealer(buyer)}
                className="text-blue-600 hover:text-blue-900 text-sm"
              >
                View
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Smaller Pagination Controls */}
      <div className="mt-6 flex items-center justify-center space-x-3">
        <button
          disabled={page <= 1}
          onClick={() => setPage(page - 1)}
          className={`px-3 py-1 rounded text-xs font-semibold transition duration-300 ease-in-out shadow ${
            page <= 1
              ? 'bg-gray-300 text-gray-700 cursor-not-allowed'
              : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700'
          }`}
        >
          Prev
        </button>
        <span className="text-sm text-gray-800">
          {page} / {totalPages}
        </span>
        <button
          disabled={page >= totalPages}
          onClick={() => setPage(page + 1)}
          className={`px-3 py-1 rounded text-xs font-semibold transition duration-300 ease-in-out shadow ${
            page >= totalPages
              ? 'bg-gray-300 text-gray-700 cursor-not-allowed'
              : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700'
          }`}
        >
          Next
        </button>
      </div>

      {/* Modal for Dealer Details */}
      {modalVisible && selectedDealer && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full p-6 relative">
            <h2 className="text-2xl font-bold mb-4">Dealer Details</h2>
            <div className="space-y-2 text-gray-700">
              <p><strong>ID:</strong> {selectedDealer.id || '-'}</p>
              <p><strong>Business Name:</strong> {selectedDealer.businessName || '-'}</p>
              <p><strong>Business Owner:</strong> {selectedDealer.businessOwner || '-'}</p>
              <p><strong>Manager Name:</strong> {selectedDealer.managerName || '-'}</p>
              <p><strong>Primary Phone:</strong> {selectedDealer.phone || '-'}</p>
              <p><strong>Secondary Phone:</strong> {selectedDealer.secondaryPhone || '-'}</p>
              <p><strong>Email:</strong> {selectedDealer.email || '-'}</p>
              <p><strong>GST Number:</strong> {selectedDealer.gstNo || '-'}</p>
              <p><strong>PAN Number:</strong> {selectedDealer.panNo || '-'}</p>
              <p><strong>Address:</strong> {selectedDealer.address || '-'}</p>
              <p><strong>Pin Code:</strong> {selectedDealer.pinCode || '-'}</p>
              <p><strong>Station:</strong> {selectedDealer.station?.name || '-'}</p>
            </div>
            <button
              onClick={() => setModalVisible(false)}
              className="mt-6 px-4 py-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition duration-300 ease-in-out shadow"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuyerManagement;
